import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, X, ArrowClockwise } from '@phosphor-icons/react';
import { list3plCharges, create3plManualCharge, void3plCharge } from '../../lib/api';
import { fmt$, fmtDate } from './utils';

export default function ChargesTab({ selected, merchants, onRefresh }) {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: 'open', type: '' });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ merchant_id: '', type: 'vas', description: '', qty: 1, unit_price: 0 });
  const [saving, setSaving] = useState(false);

  const fetchCharges = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selected) params.merchant_id = selected.id;
      if (filter.status) params.status = filter.status;
      if (filter.type) params.type = filter.type;
      const r = await list3plCharges(params);
      setCharges(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load charges');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchCharges(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selected, filter.status, filter.type]);

  const total = useMemo(() => charges.reduce((s, c) => s + (Number(c.amount) || 0), 0), [charges]);

  const beginAdd = () => {
    setForm({
      merchant_id: selected?.id || merchants[0]?.id || '',
      type: 'vas', description: '', qty: 1, unit_price: 0,
    });
    setOpen(true);
  };
  const submit = async () => {
    if (!form.merchant_id) { toast.error('Merchant required'); return; }
    if (!form.description.trim()) { toast.error('Description required'); return; }
    setSaving(true);
    try {
      await create3plManualCharge({ ...form, description: form.description.trim() });
      toast.success('Charge added');
      setOpen(false);
      fetchCharges();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally {
      setSaving(false);
    }
  };
  const voidIt = async (c) => {
    if (!window.confirm(`Void charge ${fmt$(c.amount)} (${c.description})?`)) return;
    try {
      await void3plCharge(c.id);
      toast.success('Charge voided');
      fetchCharges();
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Void failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold">Charges{selected ? ` · ${selected.name}` : ' · all merchants'}</h2>
        <div className="flex items-center gap-2">
          <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="border border-border bg-background px-2 py-1 text-sm" data-testid="charge-filter-status">
            <option value="open">Open</option>
            <option value="invoiced">Invoiced</option>
            <option value="voided">Voided</option>
            <option value="">All</option>
          </select>
          <select value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}
            className="border border-border bg-background px-2 py-1 text-sm" data-testid="charge-filter-type">
            <option value="">All types</option>
            <option value="storage">Storage</option>
            <option value="inbound">Inbound</option>
            <option value="outbound">Outbound</option>
            <option value="vas">VAS</option>
            <option value="minimum">Minimum</option>
            <option value="adjustment">Adjustment</option>
          </select>
          <Button size="sm" variant="outline" onClick={fetchCharges} data-testid="charges-refresh">
            <ArrowClockwise size={14} />
          </Button>
          <Button size="sm" onClick={beginAdd} data-testid="charge-add-btn">
            <Plus size={14} className="mr-1" /> Manual charge
          </Button>
        </div>
      </div>
      <div className="text-xs text-muted-foreground">
        {charges.length} charge(s) · total <span className="font-mono font-bold text-foreground">{fmt$(total)}</span>
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2">When</th>
              <th className="p-2">Merchant</th>
              <th className="p-2">Type</th>
              <th className="p-2">Description</th>
              <th className="p-2 text-right">Qty</th>
              <th className="p-2 text-right">Unit</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2">Status</th>
              <th className="p-2 w-20 text-right">·</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Loading…</td></tr>}
            {!loading && charges.length === 0 && (
              <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">
                No charges. Auto-accrual fires when orders ship and receipts complete (configured rate cards required).
              </td></tr>
            )}
            {charges.map((c) => {
              const merchant = merchants.find((m) => m.id === c.merchant_id);
              return (
                <tr key={c.id} className="border-b border-border last:border-b-0" data-testid={`charge-row-${c.id}`}>
                  <td className="p-2 text-xs">{fmtDate(c.accrued_at)}</td>
                  <td className="p-2">{merchant?.name || c.merchant_id}</td>
                  <td className="p-2"><span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-slate-100 text-slate-700">{c.type}</span></td>
                  <td className="p-2 text-xs max-w-md truncate">{c.description}</td>
                  <td className="p-2 text-right font-mono text-xs">{c.qty}</td>
                  <td className="p-2 text-right font-mono text-xs">{fmt$(c.unit_price)}</td>
                  <td className="p-2 text-right font-mono font-bold">{fmt$(c.amount)}</td>
                  <td className="p-2">
                    <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 ${c.status === 'open' ? 'bg-amber-100 text-amber-800' : c.status === 'invoiced' ? 'bg-sky-100 text-sky-800' : 'bg-slate-200 text-slate-600'}`}>
                      {c.status}
                    </span>
                  </td>
                  <td className="p-2 text-right">
                    {c.status === 'open' && (
                      <button type="button" onClick={() => voidIt(c)} title="Void" className="text-rose-600 hover:text-rose-800" data-testid={`charge-void-${c.id}`}>
                        <X size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="charge-dialog">
          <DialogHeader>
            <DialogTitle>Add manual charge</DialogTitle>
            <DialogDescription>VAS / adjustment / one-off — bypasses auto-accrual.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Merchant</Label>
              <select className="w-full border border-border bg-background px-2 py-2 text-sm"
                value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                data-testid="charge-form-merchant">
                {merchants.filter((m) => m.status === 'active').map((m) => (
                  <option key={m.id} value={m.id}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <select className="w-full border border-border bg-background px-2 py-2 text-sm"
                  value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                  data-testid="charge-form-type">
                  <option value="vas">VAS</option>
                  <option value="adjustment">Adjustment</option>
                  <option value="storage">Storage</option>
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                  <option value="credit">Credit</option>
                </select>
              </div>
              <div>
                <Label>Qty</Label>
                <Input type="number" step="0.01" value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: parseFloat(e.target.value) || 0 })}
                  data-testid="charge-form-qty" />
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="charge-form-description" />
            </div>
            <div>
              <Label>Unit price ($)</Label>
              <Input type="number" step="0.01" value={form.unit_price}
                onChange={(e) => setForm({ ...form, unit_price: parseFloat(e.target.value) || 0 })}
                data-testid="charge-form-unit-price" />
              <div className="text-[11px] text-muted-foreground mt-1">
                Total: <span className="font-mono font-bold">{fmt$((form.qty || 0) * (form.unit_price || 0))}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="charge-form-submit">
              {saving ? 'Saving…' : 'Add charge'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
